import type { PlanRow } from 'modules/campaign/row';
import type { BudgetGroup, ReorderRequest } from '../types';
import type { PlanStore } from './plan';

export function optimisticReorder(current: PlanStore, request: ReorderRequest): PlanStore {
  const { subjectId, subjectType, targetId, targetType, position } = request;
  const isSubjectGroup = subjectType === 'GROUP';

  const subject = isSubjectGroup ? current.groups[subjectId] : current.rows[subjectId];
  const target = targetType === 'GROUP' ? current.groups[targetId] : current.rows[targetId];

  // Calculate new parent
  let newParentId: number | null;

  if (position === 'INSIDE') {
    newParentId = targetId;
  } else {
    newParentId = targetType === 'GROUP' ? (target as BudgetGroup).parent_id : (target as PlanRow).group_id;
  }

  // select order space
  const allSiblings = [
    ...Object.values(current.groups).filter((g) => g.parent_id === newParentId && g.id !== subjectId),
    ...Object.values(current.rows).filter((r) => r.group_id === newParentId && r.id !== subjectId),
  ].sort((a, b) => a.order_index - b.order_index);

  let newOrderIndex: number;
  const GAP = 65536.0;

  if (position === 'INSIDE') {
    // Place as last child
    if (allSiblings.length === 0) {
      newOrderIndex = GAP;
    } else {
      newOrderIndex = allSiblings[allSiblings.length - 1].order_index + GAP;
    }
  } else if (position === 'ABOVE') {
    const targetIndex = allSiblings.findIndex((s) => s.id === targetId);
    if (targetIndex === 0) {
      // Place before first sibling
      newOrderIndex = allSiblings[0].order_index - GAP;
    } else {
      // Place between prev and target
      const prev = allSiblings[targetIndex - 1];
      newOrderIndex = (prev.order_index + target.order_index) / 2;
    }
  } else {
    // BELOW
    const targetIndex = allSiblings.findIndex((s) => s.id === targetId);
    if (targetIndex === allSiblings.length - 1) {
      // Place after last sibling
      newOrderIndex = allSiblings[allSiblings.length - 1].order_index + GAP;
    } else {
      // Place between target and next
      const next = allSiblings[targetIndex + 1];
      newOrderIndex = (target.order_index + next.order_index) / 2;
    }
  }

  const updatedSubject = {
    ...subject,
    ...(isSubjectGroup ? { parent_id: newParentId } : { group_id: newParentId }),
    order_index: newOrderIndex,
  };

  return {
    ...current,
    ...(isSubjectGroup
      ? { groups: { ...current.groups, [subjectId]: updatedSubject } }
      : { rows: { ...current.rows, [subjectId]: updatedSubject } }),
  } as PlanStore;
}
